import React, { useState, useMemo } from "react";
import { useParams } from "react-router";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { HStack, VStack } from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import { useGetBeneficialInvestorDetails } from "../../queries/shareholders";
import { getCurrencyType } from "../../utils/currencyFormatter";
import { getCurrencySymbolRoboto } from "../../utils/currencyRoboto";
import Loader from "../../shared/Loader";
import { formatDisplayDate } from "../../utils/date";
import {
  FirstCardComponent,
  SecondCardComponent,
} from "../../components/shared/InnerPecentageCardDesign";
import { useCanUserAccess } from "../../store/useRoleGrantStore";
import {
  ButtonSize,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import ShareModal from "../share/ShareModal";
import { useShareAuthStore } from "../../store/useShareAuthStore";
import { useSharedBeneficiary } from "../../queries/sharedResouces";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";

function BeneficiaryTopCard({
  beneficiaryDetails,
}: {
  beneficiaryDetails: any;
}) {
  const currency = getCurrencyType();
  const currencySymbol = getCurrencySymbolRoboto();

  const totalShares =
    beneficiaryDetails?.shareCertificateDetails?.reduce(
      (sum: number, cert: any) => sum + (cert.numberOfShares || 0),
      0
    ) || 0;

  const totalPercentage =
    beneficiaryDetails?.shareCertificateDetails?.length > 0
      ? beneficiaryDetails.shareCertificateDetails[0].percentage
      : 0;

  // Get share certificate numbers
  const shareCertificateNumbers =
    beneficiaryDetails?.shareCertificateDetails?.map(
      (cert: any) => cert.certificateId || cert.id || cert.shareNumber || "N/A"
    ) || [];

  // Create data structure for FirstCardComponent
  const firstCardData = {
    name: beneficiaryDetails?.name || "Unknown",
    type: "OTHERS",
    percentage: totalPercentage * 100,
    isPromoter: false,
    isInstitutional: false,
  };
  console.log("First Card Data:", firstCardData, beneficiaryDetails);

  // Create data structure for SecondCardComponent
  const secondCardData = {
    shareholderDetails: {
      id: beneficiaryDetails?.id || "",
      name: beneficiaryDetails?.name || "",
      type: "Beneficiary",
      holdingPercentage: totalPercentage || 0,
      shares: totalShares || 0,
      fdbShares: 0,
      equityShares: totalShares || 0,
      prefShares: 0,
      debentures: 0,
      pricePerShare: 1, // Placeholder
      roundIdentifierDetails: [],
      currentPrice: 1,
      gain: 0,
      networth: beneficiaryDetails?.netWorth || 0,
      netWorthPercentage: 0,
      shareRanges: shareCertificateNumbers,
      securityTypes: beneficiaryDetails?.shareCertificateDetails?.map(
        (cert: any) => cert.securityClass || "Common"
      ) || ["Common"],
      holdingPercent: totalPercentage || 0,
      netWorth: beneficiaryDetails?.netWorth || 0,
      sharesPerSecurity: [],
    },
  };

  return (
    <div className="pb-8">
      <HStack className="flex-col w-full pb-8 overflow-auto md:flex-row scrollbar-hide">
        <HStack className="justify-center flex-1 gap-0">
          <FirstCardComponent data={firstCardData} isInvestorPortal={false} />
          <SecondCardComponent
            isInvestorPortal={false}
            cardDetails={secondCardData}
            showCertificateNumber={false}
            cardType="shareholder"
          />
        </HStack>
      </HStack>
    </div>
  );
}

function BeneficiaryDetailsCard({
  beneficiaryDetails,
}: {
  beneficiaryDetails: any;
}) {
  const [activeTab, setActiveTab] = useState<"contact" | "documents">(
    "contact"
  );
  const [pdfDialog, setPdfDialog] = useState<{
    open: boolean;
    url?: string;
    name?: string;
  }>({ open: false });

  // Helper function to check if file is PDF
  const isPdfFile = (fileName: string) => fileName?.toLowerCase().endsWith(".pdf");

  // Get all documents from share certificates
  const allDocuments =
    beneficiaryDetails?.shareCertificateDetails?.reduce(
      (docs: any[], cert: any) => {
        if (cert.documents && cert.documents.length > 0) {
          return [...docs, ...cert.documents];
        }
        return docs;
      },
      []
    ) || [];

  return (
    <div className="mx-4 bg-gradient-to-b from-blue-50 to-white border rounded-lg shadow-sm">
      {/* Tab Navigation */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab("contact")}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === "contact"
              ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <HStack className="items-center justify-center gap-2">
            <Icon icon="mdi:account-circle" width="16" />
            Contact Information
          </HStack>
        </button>
        <button
          onClick={() => setActiveTab("documents")}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === "documents"
              ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <HStack className="items-center justify-center gap-2">
            <Icon icon="mdi:file-document" width="16" />
            Documents ({allDocuments.length})
          </HStack>
        </button>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === "contact" && (
          <VStack className="gap-4">
            <HStack className="items-center gap-3">
              <Icon icon="mdi:person" className="text-gray-500" width="16" />
              <VStack className="gap-0">
                <p className="text-xs font-medium text-gray-600">Name</p>
                <p className="text-sm">{beneficiaryDetails?.name || "-"}</p>
              </VStack>
            </HStack>
            <HStack className="items-center gap-3">
              <Icon icon="mdi:email" className="text-gray-500" width="16" />
              <VStack className="gap-0">
                <p className="text-xs font-medium text-gray-600">Email</p>
                <p className="text-sm">{beneficiaryDetails?.email || "-"}</p>
              </VStack>
            </HStack>
            <HStack className="items-center gap-3">
              <Icon
                icon="mdi:map-marker"
                className="text-gray-500"
                width="16"
              />
              <VStack className="gap-0">
                <p className="text-xs font-medium text-gray-600">Address</p>
                <p className="text-sm">{beneficiaryDetails?.address || "-"}</p>
              </VStack>
            </HStack>
            <HStack className="items-center gap-3">
              <Icon icon="mdi:calendar" className="text-gray-500" width="16" />
              <VStack className="gap-0">
                <p className="text-xs font-medium text-gray-600">
                  Allotment Date
                </p>
                <p className="text-sm">
                  {beneficiaryDetails?.date
                    ? formatDisplayDate(beneficiaryDetails.date)
                    : "-"}
                </p>
              </VStack>
            </HStack>
          </VStack>
        )}

        {activeTab === "documents" && (
          <div>
            {allDocuments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Document Name
                      </th>
                      <th className="text-left py-3 px-2 text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="text-center py-3 px-2 text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allDocuments.map((doc: any, index: number) => (
                      <tr
                        key={doc.id || index}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-2">
                          <HStack className="items-center gap-2">
                            <Icon
                              icon={
                                isPdfFile(doc.name || doc.fileName || "")
                                  ? "mdi:file-pdf-box"
                                  : "mdi:file-document"
                              }
                              className={
                                isPdfFile(doc.name || doc.fileName || "")
                                  ? "text-red-500"
                                  : "text-blue-500"
                              }
                              width="16"
                            />
                            <VStack className="gap-0">
                              <p className="text-sm font-medium text-gray-900">
                                {doc.name ||
                                  doc.fileName ||
                                  `Document ${index + 1}`}
                              </p>
                              {doc.description && (
                                <p className="text-xs text-gray-500">
                                  {doc.description}
                                </p>
                              )}
                            </VStack>
                          </HStack>
                        </td>
                        <td className="py-3 px-2">
                          <p className="text-sm text-gray-600">
                            {doc.createdAt
                              ? formatDisplayDate(doc.createdAt)
                              : "-"}
                          </p>
                        </td>
                        <td className="py-3 px-2">
                          <HStack className="justify-center gap-2">
                            {doc.url && (
                              <>
                                {isPdfFile(doc.name || doc.fileName || "") ? (
                                  <button
                                    onClick={() =>
                                      setPdfDialog({
                                        open: true,
                                        url: doc.url,
                                        name:
                                          doc.name ||
                                          doc.fileName ||
                                          `Document ${index + 1}`,
                                      })
                                    }
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                                    title="View PDF"
                                  >
                                    <Icon icon="mdi:file-pdf-box" width="12" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() =>
                                      window.open(doc.url, "_blank")
                                    }
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                                    title="View Document"
                                  >
                                    <Icon icon="mdi:eye" width="12" />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    const link = document.createElement("a");
                                    link.href = doc.url;
                                    link.download =
                                      doc.name ||
                                      doc.fileName ||
                                      `document-${index + 1}`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                  className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
                                  title="Download Document"
                                >
                                  <Icon icon="mdi:download" width="12" />
                                </button>
                              </>
                            )}
                          </HStack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon
                  icon="mdi:file-document-outline"
                  className="text-gray-400 mx-auto mb-2"
                  width="48"
                />
                <p className="text-gray-500">No documents available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfDialog.open} maxWidth="md">
        {pdfDialog.url && (
          <LazyPdfDocument
            url={pdfDialog.url}
            name={pdfDialog.name}
            onClose={() => setPdfDialog({ open: false })}
          />
        )}
      </Dialog>
    </div>
  );
}

export function BeneficiaryWrapper() {
  const { id } = useParams();
  console.log("Beneficiary ID:", id);
  const pathName = window.location.href.split("/");
  const sharedPage = useMemo(() => pathName[3] === "share", [pathName]);
  const { shareResourceId } = useShareAuthStore();

  const canShareBeneficiary = useCanUserAccess("create", "shareholder");
  const [showShare, setShowShare] = useState(false);

  // Use shared beneficiary data if it's a shared page, otherwise use regular data
  const beneficiaryId = sharedPage ? shareResourceId : id;

  const {
    data: beneficiaryDetails,
    isLoading,
    error,
  } = sharedPage
    ? useSharedBeneficiary(beneficiaryId || "")
    : useGetBeneficialInvestorDetails(beneficiaryId || "");

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-500">Error loading beneficiary details</p>
      </div>
    );
  }

  if (!beneficiaryDetails) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-500">No beneficiary details found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showShare && (
        <ShareModal
          resourceId={id || ""}
          resourceType={"BENEFICIARY"}
          close={() => setShowShare(false)}
        />
      )}

      {!sharedPage && (
        <HStack className="justify-between min-h-[45px] items-center">
          <BCHeader
            bcTitle="Beneficiaries"
            bcSubTitle={beneficiaryDetails.name}
          />

          <HStack className="gap-4 px-4">
            {canShareBeneficiary && (
              <SecondaryCTAButton
                buttonSize={ButtonSize.SMALL}
                onClick={() => {
                  setShowShare(true);
                }}
              >
                Share
              </SecondaryCTAButton>
            )}
          </HStack>
        </HStack>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Top Profile Cards */}
        <BeneficiaryTopCard beneficiaryDetails={beneficiaryDetails} />

        {/* Detailed Information Card */}
        <BeneficiaryDetailsCard beneficiaryDetails={beneficiaryDetails} />
      </div>
    </div>
  );
}

const BeneficiaryPage = () => {
  const id = useParams().id;
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto"></div>
    </div>
  );
};

export default BeneficiaryWrapper;
